/**
 * Maps document write-backs onto Candid RPA document preview tabs.
 *
 * @module mapDocumentWritebacksToPreviewTabs
 */
import { CandidDocumentPreviewTab } from "../../../pages/worklists/singleOrder/candidUpload/types/candidDocumentPreviewTab";
import {
  CandidDocumentPayload,
  CandidWritebackRecord,
} from "../../../pages/worklists/singleOrder/candidUpload/types/candidWriteback";
import { getDocumentPayload } from "../../../pages/worklists/singleOrder/candidUpload/utils/getDocumentPayload";
import { formatDate } from "../../../utils/formatDate";

/** Tab label from `created_at`. */
const TAB_LABEL_FORMAT = "MM/DD/YYYY";

/** Header timestamp from `created_at`. */
const TAB_TIME_FORMAT = "MM/DD/YYYY HH:mm:ss";

/**
 * File name from a bucket-relative GCS path.
 *
 * @param gcsPath - Object path such as `/medical_pa_order/.../file.pdf`
 * @returns Last path segment, or the original path
 */
const fileNameFromGcsPath = (gcsPath: string): string =>
  gcsPath.split("/").pop() ?? gcsPath;

/**
 * Display name for the tab header: payload name, else the GCS file name.
 *
 * @param payload - Document write-back body
 * @returns Non-empty name string
 */
const resolveDocumentName = (payload: CandidDocumentPayload): string => {
  const trimmed = payload.display_name?.trim() ?? "";
  if (trimmed) {
    return trimmed;
  }
  return fileNameFromGcsPath(payload.document_gcs_path);
};

/**
 * Maps one document write-back onto a preview tab.
 *
 * @param record - Document write-back row
 * @returns Tab model, or null when the payload is missing
 */
const mapDocumentWritebackToPreviewTab = (
  record: CandidWritebackRecord,
): CandidDocumentPreviewTab | null => {
  const payload = getDocumentPayload(record);
  if (!payload) {
    return null;
  }
  return {
    id: record.id,
    label: formatDate(record.created_at, TAB_LABEL_FORMAT),
    name: resolveDocumentName(payload),
    src: payload.document_gcs_path,
    time: formatDate(record.created_at, TAB_TIME_FORMAT),
  };
};

/**
 * Converts document write-backs into sidenav tabs, skipping rows with no body.
 *
 * @param records - Document group from `groupedByUseCase.document`
 * @returns Preview tabs in the same order as `records`
 */
export const mapDocumentWritebacksToPreviewTabs = (
  records: CandidWritebackRecord[],
): CandidDocumentPreviewTab[] =>
  records
    .map(mapDocumentWritebackToPreviewTab)
    .filter((tab): tab is CandidDocumentPreviewTab => tab !== null);
